import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from '@/components/layout/ProtectedRoute';
import ErrorBoundary from '@/components/error/ErrorBoundary';
import OfflineBanner from '@/components/error/OfflineBanner';
import LoginPage from '@/pages/LoginPage';
import ProjectsPage from '@/pages/ProjectsPage';
import IntakesPage from '@/pages/IntakesPage';
import IntakeWizardPage from '@/pages/IntakeWizardPage';
import ProjectDetailPage from '@/pages/ProjectDetailPage';
import SettingsPage from '@/pages/SettingsPage';
import ResourcesPage from '@/pages/ResourcesPage';
import UsersPage from '@/pages/UsersPage';
import CasdmPage from '@/pages/CasdmPage';
import DashboardPage from '@/pages/DashboardPage';
import { useAuth } from '@/contexts/AuthContext';
import { canViewIntakes } from '@/lib/project-permissions';
import NotFoundPage from '@/pages/NotFoundPage';
import { useIntakePolling } from '@/hooks/useIntakePolling';
import { useWebSocket } from '@/hooks/useWebSocket';
import { WebSocketOfflineBanner } from '@/components/WebSocketOfflineBanner';

/**
 * Mounts the 60s Oscar-intake poller (leadership/admin only) at the app root so new intakes
 * surface as toasts regardless of the current route. Renders nothing.
 */
function IntakeNotifications(): null {
  useIntakePolling();
  return null;
}

/**
 * Mounts the single app-wide WebSocket connection. The active project id is derived from the
 * current URL (`/projects/:projectId`) so project-scoped pushes (phase 0, chat jobs, Avoma sync)
 * are subscribed to whichever project the user is viewing; role-scoped pushes (intake arrivals)
 * arrive regardless. Renders nothing.
 */
function RealtimeConnection(): null {
  const { pathname } = useLocation();
  const activeProjectId = pathname.match(/^\/projects\/([^/]+)/)?.[1];
  useWebSocket(activeProjectId);
  return null;
}

/** How long `/` waits for the server-verified role before defaulting to /projects. */
const HOME_ROLE_WAIT_MS = 3000;
/**
 * T-44a: `/` lands leadership/admin on the Dashboard (weekly compliance + portfolio health) and
 * everyone else on Projects. The Dashboard itself is leadership/admin only.
 */
export function HomeRedirect(): JSX.Element | null {
  const { user, roleVerified } = useAuth();
  // Until the server confirms the role it is least-privileged `engineer`, so redirecting
  // immediately would send every leader to /projects. Wait briefly; if verification cannot
  // complete (server unreachable) fall back to /projects rather than hanging on a blank page.
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (roleVerified) return undefined;
    const t = window.setTimeout(() => setGaveUp(true), HOME_ROLE_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [roleVerified]);
  if (!roleVerified && !gaveUp) return null;
  const isLeadership = user?.role === 'leadership' || user?.role === 'admin';
  return <Navigate to={isLeadership ? '/dashboard' : '/projects'} replace />;
}
/** PMs have no Intakes access: a direct /intakes URL lands on /projects instead. */
function IntakesRoute({ children }: { children: JSX.Element }): JSX.Element {
  const { user } = useAuth();
  return canViewIntakes(user?.role) ? children : <Navigate to="/projects" replace />;
}

function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <OfflineBanner />
      <WebSocketOfflineBanner />
      <IntakeNotifications />
      <RealtimeConnection />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/intakes" element={<IntakesRoute><IntakesPage /></IntakesRoute>} />
          <Route
            path="/intakes/:jiraKey/review"
            element={<IntakesRoute><IntakeWizardPage /></IntakesRoute>}
          />
          <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/casdm" element={<CasdmPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
